import React from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ChevronRight, LayoutGrid } from "lucide-react";

/**
 * EmailAssetTable — DASH-EMAIL-011a, cut over in DASH-EMAIL-012A to the
 * real Asset rollup (GET creative-analytics/assets/{family}).
 *
 * The content-preview column is gone (DASH-EMAIL-012A Frozen Decision 15):
 * no asset family has a canonical preview field exposed by the real read
 * API today — this is a genuine, disclosed gap versus the mock (see the
 * 012A completion report), not something this component fabricates from
 * the asset's own governed name. The three separate illustrative metric
 * columns collapse into one real `channel2_metrics` column, same as
 * EmailAngleTable/EmailCreativeCatalogTable.
 */
function MetricCell({ metric, testId }) {
  const isUnavailable = metric?.availability === "UNAVAILABLE";
  const display = isUnavailable ? "—" : Number(metric?.value || 0).toLocaleString();
  return (
    <TableCell className="text-right font-mono text-slate-100" data-testid={testId}>
      <span data-metric-availability={isUnavailable ? "unavailable" : "available"}>{display}</span>
    </TableCell>
  );
}

export default function EmailAssetTable({ rows, family, onSelectAsset }) {
  const data = rows || [];

  if (data.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="email-asset-table-empty">
        <CardContent className="p-8 text-center text-slate-400">
          <p className="text-lg">No {family} assets for this selection</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-asset-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <LayoutGrid className="h-5 w-5 text-indigo-400" />
          Assets — {family}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-8 text-slate-400" />
              <TableHead className="text-slate-400">Asset</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-right text-slate-400">Creative Versions</TableHead>
              <TableHead className="text-right text-slate-400">Broadcasts</TableHead>
              <TableHead className="text-right text-slate-400">Channel-2 Outcomes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => {
              const rowKey = `${row.family}:${row.asset_id}@${row.version}`;
              return (
                <TableRow
                  key={rowKey}
                  className="cursor-pointer border-white/10 hover:bg-white/5"
                  data-testid={`email-asset-row-${rowKey}`}
                  onClick={() => onSelectAsset?.(row.family, row.asset_id, row.version)}
                >
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-slate-400" data-testid={`email-asset-open-${rowKey}`} />
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-100">
                      {row.name}
                      <span className="text-slate-500"> v{row.version}</span>
                    </div>
                    <div className="text-xs text-slate-400">{row.asset_id}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-300">{row.status}</TableCell>
                  <TableCell className="text-right font-mono text-slate-100">{row.creative_version_count.toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-slate-100">{row.broadcast_count.toLocaleString()}</TableCell>
                  <MetricCell metric={row.channel2_metrics} testId={`email-asset-channel2-${rowKey}`} />
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
